import { type JWTPayload, jwtVerify } from 'jose';
import jwt from 'jsonwebtoken';
import { NextApiRequest } from 'next';
import { NextRequest } from 'next/server';

import { type Maybe } from '@shared-components-web/types';

export const AUTH_SESSION_KEY = 'auth_session_token';
export const MAGIC_LINK_EXPIRATION_HOUR = 2;
export const PHONE_LOOKUP_EXPIRATION_MINUTES = 10;
export const EMAIL_OTP_LOOKUP_EXPIRATION_MINUTES = 10;
export const AUTH_INVALID_REDIRECT_ROUTE = `/user-portal?error=expired_token`;
export const AUTH_LOGOUT_KEY = 'auth_logged_out';

export const isTokenActive = (expiry: string): boolean => {
	const expiryTime = Number(expiry);
	return Date.now() < expiryTime;
};

export type TAuthTimeSlot = {
	date?: string;
	providerId?: string;
	time?: string;
};

export type TAuthTokenPayload = {
	userId?: string;
	email?: string;
	facility?: string;
	redirectTo?: string;
	timeSlot?: TAuthTimeSlot;
	mainReasonForVisit?: string;
	specificReasons?: string[];
};

export type TAuthToken = {
	userId?: string;
	email?: string;
	facility?: string;
	mainReasonForVisit?: string;
	specificReasons?: string[];
	timeSlot?: TAuthTimeSlot;
	redirectTo?: string;
	exp: number;
	iat: number;
};

export type TPhoneLookupTokenPayload = {
	userId: string;
	email: string;
	facility: string;
	phoneNumber: string;
	purpose: 'phone_lookup';
	redirectTo?: string;
	timeSlot?: TAuthTimeSlot;
	mainReasonForVisit?: string;
	specificReasons?: string[];
};

export type TPhoneLookupToken = TPhoneLookupTokenPayload & {
	exp: number;
	iat: number;
};

export type TEmailOtpLookupTokenPayload = {
	email: string;
	facility: string;
	purpose: 'email_otp_lookup';
	redirectTo?: string;
	timeSlot?: TAuthTimeSlot;
	mainReasonForVisit?: string;
	specificReasons?: string[];
};

export type TEmailOtpLookupToken = TEmailOtpLookupTokenPayload & {
	exp: number;
	iat: number;
};

type BrazeRecipientAttributes = {
	first_name: Maybe<string> | undefined;
	email?: string;
};

type BrazeCampaignRecipient = {
	external_user_id: string;
	trigger_properties: Record<string, string>;
	send_to_existing_only: boolean;
	attributes: BrazeRecipientAttributes;
};

type SendBrazeCampaignEmailProps = {
	campaignId?: string;
	recipient: BrazeCampaignRecipient;
	errorMessage: string;
};

type BrazeCampaignSendResult = {
	status: number;
	statusText: string;
	ok: boolean;
	body?: unknown;
	elapsedMs: number;
};

type BrazeExportedUser = {
	external_id?: string;
	custom_attributes?: Record<string, unknown>;
};

type BrazeUserAttributesResult = {
	status: number;
	statusText: string;
	ok: boolean;
	users: BrazeExportedUser[];
	elapsedMs: number;
};

const BRAZE_REQUEST_TIMEOUT_MS = Number(process.env.BRAZE_REQUEST_TIMEOUT_MS || '15000');

const resolveBrazeFailureReason = (error: unknown, timeoutMs: number): string => {
	if (error instanceof Error) {
		if (error.message.includes('request_timeout')) {
			return `request_timeout after ${timeoutMs}ms`;
		}

		const errorCodeRaw = (error as { code?: unknown }).code;
		const errorCode =
			typeof errorCodeRaw === 'string' || typeof errorCodeRaw === 'number' ? String(errorCodeRaw) : '';
		if (
			error instanceof TypeError ||
			['ECONNREFUSED', 'ENOTFOUND', 'EAI_AGAIN', 'ETIMEDOUT', 'ECONNRESET'].includes(errorCode)
		) {
			const errorCodeSuffix = errorCode ? ` (${errorCode})` : '';
			return `network_connect_failed${errorCodeSuffix}: ${error.message}`;
		}

		return error.message;
	}

	return 'unknown_failure';
};

export async function AuthEdgeValidateToken(token: string): Promise<JWTPayload | null> {
	try {
		const secret = process.env.MAGIC_LINK_KEY;
		if (!secret) {
			throw new Error('MAGIC_LINK_KEY environment variable not set');
		}

		const secretKey = new TextEncoder().encode(secret);
		const { payload } = await jwtVerify(token, secretKey);
		return payload;
	} catch {
		return null;
	}
}

// SERVER SIDE ONLY
export async function AuthHasExistingUserSession(req: NextRequest): Promise<boolean> {
	try {
		const token = req.cookies.get(AUTH_SESSION_KEY)?.value;
		if (!token) {
			return false;
		}
		const payload = await AuthEdgeValidateToken(token);
		return !!payload;
	} catch {
		return false;
	}
}

export function AuthServerValidateToken(req: NextApiRequest): TAuthToken | null {
	const token = typeof req.query?.token === 'string' ? req.query.token : null;
	if (!token) {
		return null;
	}

	return jwt.verify(token, process.env.MAGIC_LINK_KEY) as TAuthToken;
}

export function AuthServerGenerateToken({ payload }: { payload: TAuthTokenPayload }): string {
	const token = jwt.sign(
		{
			...payload,
		},
		process.env.MAGIC_LINK_KEY,
		{ expiresIn: `${MAGIC_LINK_EXPIRATION_HOUR}h` }
	);

	return token;
}

export function AuthServerGeneratePhoneLookupToken({ payload }: { payload: TPhoneLookupTokenPayload }): string {
	return jwt.sign(
		{
			...payload,
		},
		process.env.MAGIC_LINK_KEY,
		{ expiresIn: `${PHONE_LOOKUP_EXPIRATION_MINUTES}m` }
	);
}

export function AuthServerGenerateEmailOtpLookupToken({ payload }: { payload: TEmailOtpLookupTokenPayload }): string {
	return jwt.sign(
		{
			...payload,
		},
		process.env.MAGIC_LINK_KEY,
		{ expiresIn: `${EMAIL_OTP_LOOKUP_EXPIRATION_MINUTES}m` }
	);
}

export function AuthServerValidatePhoneLookupToken(token: string): TPhoneLookupToken | null {
	try {
		const payload = jwt.verify(token, process.env.MAGIC_LINK_KEY) as TPhoneLookupToken;
		if (payload.purpose !== 'phone_lookup') {
			return null;
		}

		return payload;
	} catch {
		return null;
	}
}

export function AuthServerValidateEmailOtpLookupToken(token: string): TEmailOtpLookupToken | null {
	try {
		const payload = jwt.verify(token, process.env.MAGIC_LINK_KEY) as TEmailOtpLookupToken;
		if (payload.purpose !== 'email_otp_lookup') {
			return null;
		}

		return payload;
	} catch {
		return null;
	}
}

const AuthServerSendBrazeCampaignEmail = async ({
	campaignId,
	recipient,
	errorMessage,
}: SendBrazeCampaignEmailProps) => {
	const endpoint = 'https://rest.iad-07.braze.com/campaigns/trigger/send';
	const startedAt = Date.now();

	const timeoutPromise = new Promise<never>((_, reject) => {
		setTimeout(() => {
			reject(new Error('request_timeout'));
		}, BRAZE_REQUEST_TIMEOUT_MS);
	});

	try {
		const result = await Promise.race([
			fetch(endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${process.env.BRAZE_API_KEY}`,
				},
				body: JSON.stringify({
					campaign_id: campaignId,
					trigger_properties: {},
					broadcast: false,
					recipients: [recipient],
				}),
			}),
			timeoutPromise,
		]);

		const responseBody =
			typeof (result as { json?: unknown }).json === 'function'
				? ((await result.json().catch(() => undefined)) as Record<string, unknown> | undefined)
				: undefined;
		const responseErrors = Array.isArray(responseBody?.errors) ? responseBody.errors : undefined;
		const serializedErrors = responseErrors ? JSON.stringify(responseErrors) : '';

		if (result.status > 299) {
			const errorSuffix = serializedErrors ? ` | errors: ${serializedErrors}` : '';
			throw new Error(`${errorMessage}: ${result.statusText}${errorSuffix}`);
		}

		if (responseErrors?.length) {
			throw new Error(`${errorMessage}: ${serializedErrors}`);
		}

		return {
			status: result.status,
			statusText: result.statusText,
			ok: result.ok,
			body: responseBody,
			elapsedMs: Date.now() - startedAt,
		} satisfies BrazeCampaignSendResult;
	} catch (e) {
		const failureReason = resolveBrazeFailureReason(e, BRAZE_REQUEST_TIMEOUT_MS);
		return new Error(`${errorMessage}: ${failureReason}`);
	}
};

export const AuthServerSendMagicLinkEmail = async ({
	clientId,
	firstName,
	magicLink,
	email,
}: {
	clientId: string;
	firstName: Maybe<string> | undefined;
	magicLink: string;
	email?: string;
}) => {
	return AuthServerSendBrazeCampaignEmail({
		campaignId: process.env.LOVET_BRAZE_CAMPAIGN_ID,
		recipient: {
			external_user_id: clientId,
			trigger_properties: {
				authenticated_user_url: magicLink,
			},
			send_to_existing_only: false,
			attributes: {
				first_name: firstName,
				email,
			},
		},
		errorMessage: 'Failed to send magic link',
	});
};

export const AuthServerSendLoginCodeEmail = async ({
	clientId,
	firstName,
	email,
	loginCode,
	campaignId = process.env.LOVET_BRAZE_CAMPAIGN_ID,
}: {
	clientId: string;
	firstName: Maybe<string> | undefined;
	email?: string;
	loginCode: string;
	campaignId?: string;
}) => {
	return AuthServerSendBrazeCampaignEmail({
		campaignId,
		recipient: {
			external_user_id: clientId,
			trigger_properties: {
				login_code: loginCode,
			},
			send_to_existing_only: false,
			attributes: {
				first_name: firstName,
				email,
			},
		},
		errorMessage: 'Failed to send login code email',
	});
};

export const normalizeSrcUrl = (srcUrl: string = globalThis.location?.href ?? ''): string => {
	// should not contain query params or hash fragments, only the path
	// should not start with a leading slash, and should end with a trailing slash
	// should not contain any protocol or domain, only the path
	// example sourceUrl: 'https://wwwstg.lovet.com/book-an-appointment/client-registration/'
	// example return 'client-registration/'

	const url = new URL(srcUrl, process.env.LOVET_MAGIC_LINK_ROUTE);
	const rawPathname = url.pathname;
	let pathnameStart = 0;
	let pathnameEnd = rawPathname.length;
	while (pathnameStart < pathnameEnd && rawPathname[pathnameStart] === '/') {
		pathnameStart += 1;
	}
	while (pathnameEnd > pathnameStart && rawPathname[pathnameEnd - 1] === '/') {
		pathnameEnd -= 1;
	}
	let pathname = rawPathname.slice(pathnameStart, pathnameEnd);
	if (pathname === 'book-an-appointment' || pathname.startsWith('book-an-appointment/')) {
		pathname = pathname.replace(/^book-an-appointment(?:\/|$)/, '');
	}
	return pathname ? `${pathname}/` : '';
};

export function AuthServerGenerateMagicLink({
	token,
	redirectUrl,
	srcUrl,
}: {
	token: string;
	redirectUrl?: string;
	srcUrl?: string;
}): string {
	const destination = normalizeSrcUrl(redirectUrl ?? srcUrl);
	const fullStr = `${process.env.LOVET_MAGIC_LINK_ROUTE}${destination}?token=${token}`;
	return fullStr;
}

export const AuthServerGetBrazeUserAttributes = async ({
	clientId,
	errorMessage = 'Failed to fetch Braze user attributes',
}: {
	clientId: string;
	errorMessage?: string;
}): Promise<BrazeUserAttributesResult | Error> => {
	// Use the SAME cluster your REST API key belongs to (curl shows iad-01).
	const endpoint = 'https://rest.iad-07.braze.com/users/export/ids';
	const startedAt = Date.now();

	if (!process.env.BRAZE_API_KEY) {
		return new Error(`${errorMessage}: BRAZE_API_KEY is not configured`);
	}

	const timeoutPromise = new Promise<never>((_, reject) => {
		setTimeout(() => {
			reject(new Error('request_timeout'));
		}, BRAZE_REQUEST_TIMEOUT_MS);
	});

	try {
		const result = await Promise.race([
			fetch(endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${process.env.BRAZE_API_KEY}`,
				},
				body: JSON.stringify({
					external_ids: [clientId],
					fields_to_export: ['external_id', 'custom_attributes'],
				}),
			}),
			timeoutPromise,
		]);
		const responseBody =
			typeof (result as { json?: unknown }).json === 'function'
				? ((await result.json().catch(() => undefined)) as Record<string, unknown> | undefined)
				: undefined;

		const responseErrors = Array.isArray(responseBody?.errors) ? responseBody.errors : undefined;
		const serializedErrors = responseErrors ? JSON.stringify(responseErrors) : '';

		if (result.status > 299) {
			const errorSuffix = serializedErrors ? ` | errors: ${serializedErrors}` : '';
			throw new Error(`${errorMessage}: ${result.status} ${result.statusText}${errorSuffix}`);
		}

		if (responseErrors?.length) {
			throw new Error(`${errorMessage}: ${serializedErrors}`);
		}
		const users = Array.isArray(responseBody?.users) ? (responseBody.users as BrazeExportedUser[]) : [];

		return {
			status: result.status,
			statusText: result.statusText,
			ok: result.ok,
			users,
			elapsedMs: Date.now() - startedAt,
		} satisfies BrazeUserAttributesResult;
	} catch (e) {
		const failureReason = resolveBrazeFailureReason(e, BRAZE_REQUEST_TIMEOUT_MS);
		return new Error(`${errorMessage}: ${failureReason}`);
	}
};
